import { useState, useCallback } from 'react';
import { MedicationFormData } from '@/components/medication/types/medication';
import { useMedicationStore } from '@/store/medicationStore';
import { useToast } from '@/hooks/use-toast';
import { v4 as uuidv4 } from 'uuid';
import { Medication, DosagePattern } from '@/types/medication';

interface MedicationFormSubmitterProps {
  formData: MedicationFormData;
  onSuccess?: (medicationData: any) => void;
  onError?: (error: Error) => void;
  editMode?: boolean;
  editId?: string;
}

/**
 * Helper component for submitting medication form data to the store
 * Handles validation, data transformation, and saving
 */
const MedicationFormSubmitter = ({
  formData,
  onSuccess,
  onError,
  editMode = false,
  editId
}: MedicationFormSubmitterProps) => {
  const { addMedication, updateMedication } = useMedicationStore();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Calculate total daily doses based on frequency and doses at time
   */
  const calculateDailyDoses = useCallback((data: MedicationFormData) => {
    let dailyMultiplier = 1;
    
    // Set multiplier based on frequency
    switch (data.frequency) {
      case 'twice-daily': dailyMultiplier = 2; break;
      case 'three-times-daily': dailyMultiplier = 3; break;
      case 'weekly': dailyMultiplier = 1/7; break;
      case 'biweekly': dailyMultiplier = 2/7; break;
      case 'monthly': dailyMultiplier = 1/30; break;
      case 'as-needed': dailyMultiplier = 0; break;
      default: dailyMultiplier = 1;
    }
    
    // Sum all doses across all time slots
    const totalDosesPerDay = Object.values(data.dosesAtTime || {}).reduce((sum: number, dose: number) => sum + dose, 0);
    
    return totalDosesPerDay * dailyMultiplier;
  }, []);

  /**
   * Calculate days supply based on daily doses and refill amount
   */
  const calculateDaysSupply = useCallback((dailyDoses: number, refillAmount?: number) => {
    if (!refillAmount || dailyDoses <= 0) return null;
    return Math.floor(refillAmount / dailyDoses);
  }, []);

  /**
   * Calculate refill date based on days supply
   */
  const calculateRefillDate = useCallback((daysSupply: number | null) => {
    if (!daysSupply) return null;
    
    const today = new Date();
    const refillDate = new Date(today);
    refillDate.setDate(today.getDate() + daysSupply);
    
    return refillDate.toISOString().split('T')[0]; // YYYY-MM-DD format
  }, []);

  /**
   * Submit the medication form data
   */
  const handleSubmit = useCallback(async () => {
    try {
      setIsSubmitting(true);
      
      // Calculate derived values for medication management
      const dailyDoses = calculateDailyDoses(formData);
      const daysSupply = calculateDaysSupply(dailyDoses, formData.refillAmount);
      const refillDate = calculateRefillDate(daysSupply);
      
      const medicationData = {
        ...formData,
        id: editMode && editId ? editId : uuidv4(),
        dailyDoses,
        daysSupply,
        refillDate,
        createdAt: editMode ? undefined : new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Get the current day of the week for consistency
      const today = new Date();
      const daysOfWeekMap = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
      const dayOfWeek = daysOfWeekMap[today.getDay()]!;
      
      // Convert MedicationFormData to Medication type that the store expects
      // Log received time of day data for debugging
      console.log('[DEBUG] MedicationFormSubmitter: Received timeOfDay:', medicationData.timeOfDay);
      
      // Ensure we have a valid time value - explicitly check each possible time period
      const selectedTime = medicationData.timeOfDay && medicationData.timeOfDay.length > 0 
        ? medicationData.timeOfDay[0] 
        : 'morning';
      
      console.log('[DEBUG] MedicationFormSubmitter: Selected time period:', selectedTime);
      
      const storeCompatibleMedication: Medication = {
        id: medicationData.id,
        name: medicationData.name,
        dosage: medicationData.dosage,
        form: medicationData.form as any, // Simple cast for now
        instructions: medicationData.notes || '',
        timing: selectedTime as any, // Use the explicitly selected time
        time: selectedTime as any, // Use the same time for both fields
        category: 'daily', // Default category
        strictTiming: medicationData.hasCycle,
        withFood: medicationData.foodInstructions === 'with-meals',
        duration: 'ongoing',
        taken: false,
        isPRN: false,
        status: 'active',
        frequency: {
          pattern: 'daily' as DosagePattern,
          daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
        },
        cycleStart: medicationData.hasCycle ? medicationData.cycleStartDate : undefined,
        cycleLength: medicationData.hasCycle ? (medicationData.cycleWeeksOn + medicationData.cycleWeeksOff) * 7 : undefined,
        activeLength: medicationData.hasCycle ? medicationData.cycleWeeksOn * 7 : undefined,
        notes: medicationData.notes,
        foodInstructions: medicationData.foodInstructions === 'with-meals' ? 'with' : 
                         medicationData.foodInstructions === 'before-meals' ? 'before' : 
                         medicationData.foodInstructions === 'after-meals' ? 'after' : undefined
      };

      console.log("[DEBUG] MedicationFormSubmitter: Mapped to store compatible medication:", storeCompatibleMedication);
      
      if (editMode && editId) {
        // Update existing medication
        updateMedication(dayOfWeek, storeCompatibleMedication);
        toast({
          title: "Medication updated",
          description: `${formData.name} has been successfully updated.`,
        });
      } else {
        // Add new medication
        console.log("[DEBUG] MedicationFormSubmitter: Adding medication to store:", storeCompatibleMedication);
        addMedication(dayOfWeek, storeCompatibleMedication);
        toast({
          title: "Medication added",
          description: `${formData.name} has been successfully added to your medications.`,
        });
      }
      
      if (onSuccess) onSuccess(medicationData);
    } catch (error: any) {
      console.error("Error submitting medication form:", error);
      toast({
        title: "Error",
        description: "There was a problem saving your medication. Please try again.",
        variant: "destructive",
      });
      
      if (onError && error instanceof Error) onError(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    formData,
    editMode,
    editId,
    addMedication,
    updateMedication,
    calculateDailyDoses,
    calculateDaysSupply,
    calculateRefillDate,
    toast,
    onSuccess,
    onError
  ]);

  return { handleSubmit, isSubmitting };
};

export default MedicationFormSubmitter;
